// src/pages/AdminEmpresaComprasPagar.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Snackbar,
  Paper,
  Avatar,
  Grid,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import EuroRoundedIcon from "@mui/icons-material/EuroRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { db } from "../contexts/firebase/config";
import { doc, getDoc, updateDoc, Timestamp } from "firebase/firestore";

function fmtMoney(n) {
  return typeof n === "number" && !Number.isNaN(n) ? n.toFixed(2) : "—";
}
function fmtDate(tsOrDate) {
  try {
    const d =
      typeof tsOrDate?.toDate === "function"
        ? tsOrDate.toDate()
        : tsOrDate instanceof Date
        ? tsOrDate
        : null;
    return d ? d.toLocaleDateString() : "—";
  } catch {
    return "—";
  }
}

export default function AdminEmpresaComprasPagar() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { empresaId, compraId } = useParams();
  const navigate = useNavigate();

  const colors = {
    accent: theme.palette.primary.main,
    surface: isDark ? "rgba(15,23,42,0.78)" : "rgba(255,255,255,0.78)",
    card: isDark ? "rgba(15,23,42,0.84)" : "rgba(255,255,255,0.84)",
    cardStrong: isDark ? "rgba(15,23,42,0.96)" : "rgba(255,255,255,0.96)",
    border: isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)",
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    hero: isDark ? "rgba(2,6,23,0.96)" : "rgba(15,23,42,0.96)",
  };

  const [dataPagamento, setDataPagamento] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [compra, setCompra] = useState(null);
  const [snack, setSnack] = useState({ open: false, severity: "success", msg: "" });

  useEffect(() => {
    let active = true;
    async function fetchCompra() {
      setLoading(true);
      try {
        const ref = doc(db, "compras", compraId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          if (active) setError("Compra não encontrada.");
        } else if (active) {
          setCompra({ id: snap.id, ...snap.data() });
        }
      } catch (err) {
        console.error(err);
        if (active) setError("Falha ao carregar dados.");
      } finally {
        if (active) setLoading(false);
      }
    }
    fetchCompra();
    return () => {
      active = false;
    };
  }, [compraId]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!compra) return;
    setError("");
    try {
      setSaving(true);
      const ref = doc(db, "compras", compraId);
      await updateDoc(ref, {
        status: "Pago",
        dataPagamento: Timestamp.fromDate(new Date(dataPagamento)),
      });
      setSnack({ open: true, severity: "success", msg: "Compra marcada como paga." });
      setTimeout(() => {
        navigate(`/admin/empresa/${empresaId}/compras`);
      }, 500);
    } catch (err) {
      console.error(err);
      setError("Não foi possível marcar como pago.");
      setSnack({ open: true, severity: "error", msg: "Falha ao marcar como pago." });
    } finally {
      setSaving(false);
    }
  }

  const InfoCard = ({ icon, label, value, highlight }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: "100%",
        borderRadius: "24px",
        border: `1px solid ${colors.border}`,
        bgcolor: colors.cardStrong,
      }}
    >
      <Stack direction="row" spacing={1.35} alignItems="center">
        <Avatar
          sx={{
            width: 46,
            height: 46,
            borderRadius: "17px",
            bgcolor: alpha(highlight ? theme.palette.success.main : colors.accent, isDark ? 0.18 : 0.1),
            color: highlight ? theme.palette.success.main : colors.accent,
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
            {label}
          </Typography>
          <Typography sx={{ fontWeight: 950, color: colors.text, lineHeight: 1.15 }} noWrap>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: isDark
            ? "linear-gradient(135deg, #020617 0%, #0f172a 52%, #111827 100%)"
            : "linear-gradient(135deg, #f8fafc 0%, #eef2ff 48%, #f8fafc 100%)",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        px: { xs: 1.25, sm: 2.5, md: 3.5 },
        py: { xs: 2, md: 3.5 },
        background: isDark
          ? "linear-gradient(135deg, #020617 0%, #0f172a 52%, #111827 100%)"
          : "linear-gradient(135deg, #f8fafc 0%, #eef2ff 48%, #f8fafc 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: isDark
            ? `radial-gradient(900px 460px at 10% 5%, ${alpha(colors.accent, 0.18)}, transparent 62%), radial-gradient(760px 440px at 92% 12%, ${alpha(theme.palette.success.main, 0.12)}, transparent 58%)`
            : `radial-gradient(900px 460px at 10% 5%, ${alpha(colors.accent, 0.16)}, transparent 62%), radial-gradient(760px 440px at 92% 12%, ${alpha(theme.palette.success.main, 0.12)}, transparent 58%)`,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: isDark ? 0.16 : 0.3,
          pointerEvents: "none",
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.16) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "linear-gradient(to bottom, black, transparent 76%)",
        }}
      />

      <Box sx={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 1180, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            borderRadius: { xs: "28px", md: "42px" },
            border: `1px solid ${colors.border}`,
            bgcolor: colors.surface,
            backdropFilter: "blur(22px)",
            WebkitBackdropFilter: "blur(22px)",
            boxShadow: isDark
              ? "0 28px 90px rgba(0,0,0,0.36)"
              : "0 24px 80px rgba(15,23,42,0.10)",
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              width: "100%",
              mb: 3,
              p: { xs: 2.5, md: 4 },
              borderRadius: "34px",
              bgcolor: colors.hero,
              color: "common.white",
              boxShadow: isDark
                ? "0 24px 70px rgba(0,0,0,0.34)"
                : "0 24px 70px rgba(15,23,42,0.22)",
              "&:before": {
                content: '""',
                position: "absolute",
                width: 560,
                height: 560,
                right: -150,
                top: -220,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${alpha(theme.palette.success.main, 0.42)} 0%, ${alpha(colors.accent, 0.16)} 42%, transparent 70%)`,
              },
            }}
          >
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={3}
              justifyContent="space-between"
              alignItems="stretch"
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "16px",
                      bgcolor: "rgba(255,255,255,0.12)",
                      color: "common.white",
                      border: "1px solid rgba(255,255,255,0.16)",
                    }}
                  >
                    <AdminPanelSettingsRoundedIcon fontSize="small" />
                  </Avatar>

                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      letterSpacing: "0.18em",
                      color: "rgba(255,255,255,0.66)",
                    }}
                  >
                    Empresa · Pagamento
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    fontSize: { xs: "2.15rem", sm: "2.85rem", md: "3.7rem" },
                    fontWeight: 950,
                    lineHeight: 0.98,
                    letterSpacing: "-0.07em",
                    color: "#ffffff",
                  }}
                >
                  Pagar Compra
                </Typography>

                <Typography
                  sx={{
                    mt: 1.7,
                    maxWidth: 820,
                    color: "rgba(255,255,255,0.68)",
                    fontSize: { xs: "0.98rem", md: "1.08rem" },
                    lineHeight: 1.75,
                  }}
                >
                  Confirma a data de pagamento para marcar esta compra como paga e fechar o registo.
                </Typography>
              </Box>

              <Stack
                direction={{ xs: "row", lg: "column" }}
                spacing={1.25}
                useFlexGap
                flexWrap="wrap"
                sx={{ width: { xs: "100%", lg: 280 }, justifyContent: "center", flexShrink: 0 }}
              >
                <Button
                  component={RouterLink}
                  to={`/admin/empresa/${empresaId}/compras`}
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    height: 42,
                    borderRadius: "999px",
                    px: 2,
                    textTransform: "none",
                    fontWeight: 950,
                    bgcolor: "rgba(255,255,255,0.12)",
                    color: "#ffffff",
                    border: "1px solid rgba(255,255,255,0.18)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.18)" },
                  }}
                >
                  Voltar
                </Button>

                <Paper
                  elevation={0}
                  sx={{
                    px: 1.6,
                    py: 1.25,
                    minWidth: { xs: "100%", sm: 220 },
                    borderRadius: "26px",
                    bgcolor: alpha(theme.palette.background.paper, isDark ? 0.14 : 0.74),
                    border: "1px solid rgba(255,255,255,0.18)",
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.66)", fontWeight: 800 }}>
                    Estado atual
                  </Typography>
                  <Typography sx={{ fontSize: "1.2rem", fontWeight: 950, color: "#ffffff" }}>
                    {compra?.status || "—"}
                  </Typography>
                </Paper>
              </Stack>
            </Stack>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: "18px" }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={5}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: { xs: 2.25, md: 2.75 },
                  borderRadius: "30px",
                  border: `1px solid ${colors.border}`,
                  bgcolor: colors.card,
                  boxShadow: isDark ? "0 24px 64px rgba(0,0,0,0.28)" : "0 24px 60px rgba(15,23,42,0.07)",
                }}
              >
                <Stack spacing={2.2}>
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "24px",
                      bgcolor: alpha(theme.palette.success.main, isDark ? 0.18 : 0.1),
                      color: theme.palette.success.main,
                    }}
                  >
                    <PaidRoundedIcon fontSize="large" />
                  </Avatar>

                  <Box>
                    <Typography variant="h5" fontWeight={950} sx={{ color: colors.text, letterSpacing: "-0.04em" }}>
                      Resumo da compra
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }}>
                      Confirma os dados antes de marcar o pagamento como concluído.
                    </Typography>
                  </Box>

                  <Divider sx={{ borderColor: colors.border }} />

                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <InfoCard icon={<PersonRoundedIcon />} label="Cliente" value={compra?.clienteNome || "—"} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InfoCard icon={<EuroRoundedIcon />} label="Valor" value={`€ ${fmtMoney(compra?.valor)}`} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InfoCard icon={<PaymentsRoundedIcon />} label="Comissão" value={`€ ${fmtMoney(compra?.comissao)}`} highlight />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InfoCard icon={<CalendarMonthRoundedIcon />} label="Data da Compra" value={fmtDate(compra?.dataCompra)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InfoCard icon={<ReceiptLongRoundedIcon />} label="Status" value={compra?.status || "—"} />
                    </Grid>
                  </Grid>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={7}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: "30px",
                  border: `1px solid ${colors.border}`,
                  bgcolor: colors.card,
                  backdropFilter: "blur(16px)",
                  WebkitBackdropFilter: "blur(16px)",
                  boxShadow: isDark ? "0 24px 64px rgba(0,0,0,0.28)" : "0 24px 60px rgba(15,23,42,0.07)",
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
                  <Stack direction="row" spacing={1.45} alignItems="center" sx={{ mb: 2.5 }}>
                    <Avatar
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: "17px",
                        bgcolor: alpha(theme.palette.success.main, isDark ? 0.18 : 0.1),
                        color: theme.palette.success.main,
                      }}
                    >
                      <CheckCircleRoundedIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={950} sx={{ color: colors.text }}>
                        Confirmar pagamento
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Define a data em que o pagamento foi realizado.
                      </Typography>
                    </Box>
                  </Stack>

                  <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, display: "grid", gap: 2.5 }}>
                    <TextField
                      label="Data do Pagamento"
                      type="date"
                      value={dataPagamento}
                      onChange={(e) => setDataPagamento(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      required
                      fullWidth
                      InputProps={{
                        startAdornment: <CalendarMonthRoundedIcon sx={{ mr: 1, color: colors.muted }} />,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "18px",
                          bgcolor: colors.cardStrong,
                          "& fieldset": { borderColor: colors.border },
                          "&:hover fieldset": { borderColor: alpha(theme.palette.success.main, 0.38) },
                          "&.Mui-focused fieldset": { borderColor: theme.palette.success.main, borderWidth: 1 },
                        },
                        "& .MuiInputLabel-root.Mui-focused": { color: theme.palette.success.main },
                      }}
                    />

                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 2.25, md: 2.75 },
                        borderRadius: "26px",
                        border: `1px solid ${alpha(theme.palette.success.main, isDark ? 0.22 : 0.14)}`,
                        bgcolor: alpha(theme.palette.success.main, isDark ? 0.12 : 0.06),
                      }}
                    >
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 900 }}>
                            Ação final
                          </Typography>
                          <Typography sx={{ fontSize: { xs: "1.3rem", md: "1.55rem" }, fontWeight: 950, color: colors.text }}>
                            Marcar compra como paga
                          </Typography>
                        </Box>
                        <Chip
                          icon={<CheckCircleRoundedIcon />}
                          label="Status: Pago"
                          sx={{
                            borderRadius: "999px",
                            fontWeight: 950,
                            bgcolor: colors.cardStrong,
                            color: theme.palette.success.main,
                            "& .MuiChip-icon": { color: "inherit" },
                          }}
                        />
                      </Stack>
                    </Paper>

                    <Divider sx={{ borderColor: colors.border }} />

                    <CardActions sx={{ px: 0, py: 0 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={saving || !compra}
                        startIcon={<SaveRoundedIcon />}
                        endIcon={!saving ? <ArrowForwardRoundedIcon /> : undefined}
                        sx={{
                          py: 1.3,
                          borderRadius: "999px",
                          textTransform: "none",
                          fontWeight: 950,
                          bgcolor: theme.palette.success.main,
                          boxShadow: "0 18px 38px rgba(34,197,94,0.24)",
                          "&:hover": { bgcolor: theme.palette.success.dark },
                        }}
                      >
                        {saving ? "A marcar..." : "Marcar como Pago"}
                      </Button>
                    </CardActions>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          variant="filled"
          sx={{ borderRadius: "16px" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
